import * as CL from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { imageUrl } from '../../common/constants/ProductOfferingConstants.js';
import { ShoppingBasketCoCoScope } from '../../common/enums.js';
import { parseBasketJson, useShoppingBasketCoCo } from '../../common/hooks/useShoppingBasketCoCo.js';
import { continueShoppingMsg, quantityMsg, t } from '../../common/i18n';
import { getLocalizedString } from '../../common/utils/productOfferingsUtils';
import { Picture } from '../Picture/Picture';
import {
  findBasketItemByCartItemId,
  findBasketItemById,
  formatTotalPrices,
  getAddonsDisplayData,
  getProductPriceToDisplay,
} from '../ShoppingBasket/shoppingBasketHelpersCoCo.js';
import { BASKET_ITEM_ADDED_EVENT } from './basketPopupEvents.js';
import { resolveAriaSummary, resolveI18nSummary } from './headerShoppingBasketUtil.js';
import type {
  BasketItemAddedDetailCoCo,
  ProductOfferingCartItems,
  ShoppingBasketType,
} from '../../common/types/shoppingBasketCoCo.js';

export interface HeaderShoppingBasketProps {
  toShoppingCart: string;
  scope?: ShoppingBasketCoCoScope;
}

interface BasketItemUpdate {
  id: string;
  cartItemId?: string;
  quantity: number;
}

const ProductImage = ({ basketItem }: { basketItem: ProductOfferingCartItems }) => (
  <Picture
    src={imageUrl}
    alt={getLocalizedString(basketItem.name)}
    offerWidthAlternatives={[94]}
    renderedImageSize={{ onPhone: '100vw' }}
  />
);

export const HeaderShoppingBasketCoCo = (props: HeaderShoppingBasketProps) => {
  const navigate = useNavigate();
  const { toShoppingCart, scope = ShoppingBasketCoCoScope.ADMIN } = props;
  const { shoppingBasket: shoppingBasketAsStr } = useShoppingBasketCoCo(scope);

  const shoppingBasket: ShoppingBasketType = parseBasketJson(shoppingBasketAsStr);

  const [lastItemChanged, setLastItemChanged] = useState<BasketItemUpdate | undefined>(undefined);
  const [pendingBasketEvent, setPendingBasketEvent] = useState<BasketItemAddedDetailCoCo | null>(null);
  const lastModifiedRef = useRef<number>(0);

  const basketItems = shoppingBasket.productOfferings;

  // Listen for basket-item-added events dispatched by addToBasket
  useEffect(() => {
    const handler = (e: Event) => {
      setPendingBasketEvent((e as CustomEvent<BasketItemAddedDetailCoCo>).detail);
    };
    window.addEventListener(BASKET_ITEM_ADDED_EVENT, handler);
    return () => window.removeEventListener(BASKET_ITEM_ADDED_EVENT, handler);
  }, []);

  // Process pending basket event after basket data is ready
  useEffect(() => {
    if (!pendingBasketEvent) {
      return;
    }

    // Resolve the exact newly added entry; looking up by offer ID can show another entry's device selection.
    const basketItem = pendingBasketEvent.cartItemId
      ? findBasketItemByCartItemId(basketItems, pendingBasketEvent.cartItemId)
      : findBasketItemById(basketItems, pendingBasketEvent.id);
    if (!basketItem) {
      // Basket data hasn't arrived yet — wait for next render
      return;
    }

    lastModifiedRef.current = Date.now();
    setLastItemChanged({
      id: pendingBasketEvent.id,
      cartItemId: pendingBasketEvent.cartItemId,
      quantity: pendingBasketEvent.quantity,
    });
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

    setPendingBasketEvent(null);
  }, [pendingBasketEvent, shoppingBasketAsStr, basketItems]);

  const getProducts = (lastItemChangedInBasket: BasketItemUpdate) => {
    const basketItem = lastItemChangedInBasket.cartItemId
      ? findBasketItemByCartItemId(basketItems, lastItemChangedInBasket.cartItemId)
      : findBasketItemById(basketItems, lastItemChangedInBasket.id);
    if (!basketItem) {
      return [];
    }
    const productPrice = getProductPriceToDisplay(
      basketItem.productOfferingPrice ?? [],
      lastItemChangedInBasket.quantity
    );
    const addons = getAddonsDisplayData(basketItem);

    return [
      {
        addons,
        id: basketItem.id,
        name: getLocalizedString(basketItem.name),
        quantity: lastItemChangedInBasket.quantity,
        price: productPrice,
        image: <ProductImage basketItem={basketItem} />,
      },
    ];
  };

  const totalProductQuantity = shoppingBasket.productOfferings.length;
  return (
    <div>
      <CL.HeaderShoppingCart
        ariaSummary={resolveAriaSummary(lastItemChanged?.quantity || 0, totalProductQuantity)}
        i18nClose={t.VLZR(continueShoppingMsg)}
        i18nQuantity={t.M0W7(quantityMsg)}
        i18nSubmit={t.VVQP('To shopping cart')}
        i18nSummary={resolveI18nSummary(totalProductQuantity)}
        i18nTitle={t.VJK6('Added to shopping cart')}
        i18nButtonLabel={t.BE8Q('Shopping cart')}
        lastChange={lastModifiedRef.current}
        products={lastItemChanged ? getProducts(lastItemChanged) : []}
        totals={formatTotalPrices(basketItems)}
        onSubmit={() => navigate(toShoppingCart)}
        totalProductQuantity={totalProductQuantity}
      />
    </div>
  );
};
